import { applyDoctorHonorificDedupeToBodyValues } from './whatsappDoctorHonorific';

export function buildPreviewMessage(
  templatePreview: string,
  bodyValues: string[],
  slots?: Array<{ slot?: number; contextKey?: string | null }>
): string {
  let msg = templatePreview || 'Hello {{1}}, your message is ready.';
  const dedupedValues = applyDoctorHonorificDedupeToBodyValues({
    bodyPreview: templatePreview,
    slots,
    values: bodyValues,
  });

  dedupedValues.forEach((val, i) => {
    msg = msg.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, 'g'), val || `{{${i + 1}}}`);
  });
  return msg;
}

/** Full name for template greetings (Hi {{1}},) — includes titles like Dr. */
export function recipientGreetingName(displayName?: string | null): string {
  const trimmed = String(displayName || '').trim();
  if (!trimmed) return 'Customer';
  return trimmed;
}

/** Build preview values with auto-filled name slots. */
export function buildPreviewBodyValues(
  bodyValues: string[],
  autoPersonalizeIndices: number[],
  previewRecipientName?: string | null
): string[] {
  const out = [...bodyValues];
  if (autoPersonalizeIndices.includes(0)) {
    while (out.length < 1) out.push('');
    out[0] = recipientGreetingName(previewRecipientName);
  }
  return out;
}

/** Default: {{1}} is the recipient name when the template has variables. */
export function defaultAutoPersonalizeIndices(variableCount: number): number[] {
  return variableCount > 0 ? [0] : [];
}

export function isMediaHeaderFormat(headerFormat?: string | null): boolean {
  const fmt = String(headerFormat || '').toUpperCase();
  return fmt === 'IMAGE' || fmt === 'VIDEO' || fmt === 'DOCUMENT';
}

export function headerFormatAcceptAttribute(headerFormat?: string | null): string {
  const fmt = String(headerFormat || '').toUpperCase();
  if (fmt === 'IMAGE') return 'image/*';
  if (fmt === 'VIDEO') return 'video/*,.mp4,.3gp';
  if (fmt === 'DOCUMENT') return '.pdf,.doc,.docx,application/pdf,application/msword';
  return 'image/*,video/*,.mp4,.3gp,.pdf,.doc,.docx';
}

/** Guess friendly labels from template preview text around {{n}} placeholders. */
export function inferVariableLabels(bodyPreview: string, count: number): string[] {
  const labels: string[] = [];
  for (let i = 1; i <= count; i += 1) {
    const re = new RegExp(`([\\w\\s]{0,30})\\{\\{${i}\\}\\}([\\w\\s]{0,30})`, 'i');
    const match = bodyPreview.match(re);
    if (match) {
      const hint = `${match[1] || ''}${match[2] || ''}`.replace(/\s+/g, ' ').trim();
      labels.push(hint ? `Value for "${hint.slice(0, 40)}"` : `Message field ${i}`);
    } else {
      labels.push(`Message field ${i}`);
    }
  }
  return labels;
}

export const WA_ATTACHMENT_LIMITS = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 16 * 1024 * 1024,
  default: 16 * 1024 * 1024,
} as const;

export function getAttachmentLimit(mimeType: string): number {
  if (mimeType.startsWith('image/')) return WA_ATTACHMENT_LIMITS.image;
  if (mimeType.startsWith('video/')) return WA_ATTACHMENT_LIMITS.video;
  if (mimeType === 'application/pdf' || mimeType.startsWith('application/')) {
    return WA_ATTACHMENT_LIMITS.document;
  }
  return WA_ATTACHMENT_LIMITS.default;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function recipientKey(countryCode: string, phoneNumber: string): string {
  return `${countryCode}|${phoneNumber}`;
}

/** Normalize user-entered phone to API payload fields. */
export function normalizeContactPhoneInput(raw: string): {
  countryCode: string;
  phoneNumber: string;
} | null {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return null;

  if (digits.startsWith('91') && digits.length >= 12) {
    return { countryCode: '+91', phoneNumber: digits.slice(-10) };
  }
  if (digits.length === 10) {
    return { countryCode: '+91', phoneNumber: digits };
  }
  if (digits.length > 10) {
    return { countryCode: '+91', phoneNumber: digits.slice(-10) };
  }
  return null;
}

/** Build a valid media-asset key for WhatsApp header attachments. */
export function buildWhatsAppAttachmentMediaKey(file: File): string {
  const stem = file.name.replace(/\.[^.]+$/, '').trim() || 'attachment';
  const normalized = stem
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 32);
  const suffix = String(Date.now()).slice(-10);
  let key = `wa_${normalized || 'attachment'}_${suffix}`.replace(/_+/g, '_');
  if (key.length > 64) key = key.slice(0, 64).replace(/_+$/, '');
  if (!/^[a-z][a-z0-9_]{2,63}$/.test(key)) {
    key = `wa_attachment_${suffix}`;
  }
  return key;
}
